// src/pages/customer-panel/chat/ChatHistoryPage.js
// Customer Panel - Admin-only Chat History Viewer (two-panel: user list + messages)

import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import axiosInstance from '../../../utils/axiosInstance';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Alert,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
    InputAdornment,
    TextField,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';
const GREEN_LIGHT = '#d1fae5';

export default function ChatHistoryPage() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || 'customer';
    const isAdmin = userRole === 'admin';

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [usersError, setUsersError] = useState('');
    const [messagesError, setMessagesError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (isAdmin) fetchUsers();
    }, []);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredUsers(users);
        } else {
            const q = searchQuery.toLowerCase();
            setFilteredUsers(
                users.filter((u) => {
                    const name = u.name || '';
                    const email = u.email || '';
                    return name.toLowerCase().includes(q) || email.toLowerCase().includes(q);
                })
            );
        }
    }, [searchQuery, users]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    if (!isAdmin) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Alert severity="warning" sx={{ fontFamily, borderRadius: '12px' }}>
                    <Typography sx={{ fontFamily, fontWeight: 600 }}>Access Denied</Typography>
                    <Typography variant="body2" sx={{ fontFamily }}>
                        Only administrators can view chat history for all users.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    const fetchUsers = async () => {
        setLoadingUsers(true);
        setUsersError('');
        try {
            const response = await axiosInstance.get('/chat/history/users');
            const data = response.data?.data || [];
            setUsers(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching chat users:', err);
            setUsersError('Failed to load users.');
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchUserMessages = async (userId) => {
        setLoadingMessages(true);
        setMessagesError('');
        try {
            const response = await axiosInstance.get(`/chat/history/user/${userId}`);
            const data = response.data?.data || [];
            setMessages(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching user messages:', err);
            setMessagesError('Failed to load messages for this user.');
        } finally {
            setLoadingMessages(false);
        }
    };

    const handleSelectUser = (chatUser) => {
        setSelectedUser(chatUser);
        setMessagesError('');
        fetchUserMessages(chatUser.id);
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
            if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        } catch {
            return '';
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2, px: 3, mb: 2, borderRadius: '16px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <HistoryIcon sx={{ fontSize: 28 }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>Chat History</Typography>
                        <Typography variant="body2" sx={{ fontFamily, opacity: 0.9 }}>View chat conversations from all users</Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Two-panel layout */}
            <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0, overflow: 'hidden' }}>
                {/* Left Panel: User List */}
                <Paper
                    elevation={0}
                    sx={{
                        width: { xs: '100%', md: 320 }, minWidth: { md: 280 },
                        borderRadius: '16px', border: '1px solid #e2e8f0',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    }}
                >
                    <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            size="small" fullWidth placeholder="Search users..."
                            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily, fontSize: '0.85rem', '&.Mui-focused fieldset': { borderColor: GREEN_ACCENT } } }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                        />
                        <Tooltip title="Refresh users">
                            <IconButton size="small" onClick={fetchUsers} disabled={loadingUsers}><RefreshIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Box>

                    {usersError && <Alert severity="error" sx={{ m: 1, fontFamily, fontSize: '0.8rem' }}>{usersError}</Alert>}

                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {loadingUsers ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: GREEN_ACCENT }} /></Box>
                        ) : filteredUsers.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <PersonIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                <Typography variant="body2" sx={{ fontFamily, color: '#94a3b8' }}>
                                    {searchQuery ? 'No users match your search.' : 'No users found.'}
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {filteredUsers.map((chatUser, idx) => {
                                    const isSelected = selectedUser?.id === chatUser.id;
                                    return (
                                        <React.Fragment key={chatUser.id}>
                                            <ListItem disablePadding>
                                                <ListItemButton
                                                    selected={isSelected}
                                                    onClick={() => handleSelectUser(chatUser)}
                                                    sx={{
                                                        py: 1.5, px: 2,
                                                        '&.Mui-selected': { bgcolor: GREEN_LIGHT, borderRight: `3px solid ${GREEN_ACCENT}`, '&:hover': { bgcolor: GREEN_LIGHT } },
                                                        '&:hover': { bgcolor: '#f8fafc' },
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar sx={{
                                                            bgcolor: isSelected ? GREEN_ACCENT : '#e2e8f0',
                                                            color: isSelected ? '#fff' : '#64748b',
                                                            fontFamily, fontWeight: 600, width: 38, height: 38, fontSize: '0.9rem',
                                                        }}>
                                                            {(chatUser.name || chatUser.email || '?').charAt(0).toUpperCase()}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={chatUser.name || chatUser.email || 'Unknown'}
                                                        secondary={chatUser.email || ''}
                                                        primaryTypographyProps={{ fontFamily, fontWeight: isSelected ? 700 : 500, fontSize: '0.85rem', color: '#1e293b', noWrap: true }}
                                                        secondaryTypographyProps={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8', noWrap: true }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                            {idx < filteredUsers.length - 1 && <Divider component="li" />}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        )}
                    </Box>
                </Paper>

                {/* Right Panel: Messages */}
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1, borderRadius: '16px', border: '1px solid #e2e8f0',
                        display: { xs: selectedUser ? 'flex' : 'none', md: 'flex' },
                        flexDirection: 'column', overflow: 'hidden',
                    }}
                >
                    {!selectedUser ? (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontFamily, fontWeight: 600, color: '#94a3b8' }}>Select a user to view chat history</Typography>
                            <Typography variant="body2" sx={{ fontFamily, color: '#cbd5e1', mt: 0.5 }}>Choose a user from the left panel to see their conversation.</Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Selected user header */}
                            <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#f8fafc' }}>
                                <Avatar sx={{ bgcolor: GREEN_ACCENT, width: 36, height: 36, fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>
                                    {(selectedUser.name || selectedUser.email || '?').charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                                        {selectedUser.name || 'Unknown User'}
                                    </Typography>
                                    {selectedUser.email && (
                                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>{selectedUser.email}</Typography>
                                    )}
                                </Box>
                                <Tooltip title="Refresh messages">
                                    <IconButton size="small" onClick={() => fetchUserMessages(selectedUser.id)} disabled={loadingMessages}>
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Messages */}
                            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {messagesError && <Alert severity="error" sx={{ fontFamily }} onClose={() => setMessagesError('')}>{messagesError}</Alert>}

                                {loadingMessages ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
                                ) : messages.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No messages found for this user.</Typography>
                                    </Box>
                                ) : (
                                    messages.map((msg, index) => {
                                        const isUser = msg.is_from_user;
                                        return (
                                            <Box
                                                key={msg.id || index}
                                                sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 1, alignItems: 'flex-start' }}
                                            >
                                                {!isUser && (
                                                    <Avatar sx={{ bgcolor: GREEN_ACCENT, width: 30, height: 30, mt: 0.5 }}>
                                                        <SmartToyIcon sx={{ fontSize: 16 }} />
                                                    </Avatar>
                                                )}
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 1.5, px: 2, maxWidth: '70%',
                                                        borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                                                        bgcolor: isUser ? GREEN_ACCENT : '#ffffff',
                                                        color: isUser ? '#ffffff' : '#1e293b',
                                                        border: isUser ? 'none' : '1px solid #e2e8f0',
                                                        '& p': { margin: 0, fontFamily, fontSize: '0.85rem', lineHeight: 1.5 },
                                                        '& a': { color: isUser ? '#e0f2fe' : '#2563eb', textDecoration: 'underline' },
                                                        '& code': { bgcolor: isUser ? 'rgba(255,255,255,0.15)' : '#f1f5f9', px: 0.5, borderRadius: '4px', fontSize: '0.78rem' },
                                                    }}
                                                >
                                                    <ReactMarkdown
                                                        rehypePlugins={[rehypeRaw]}
                                                        components={{
                                                            a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                                                        }}
                                                    >
                                                        {msg.message || ''}
                                                    </ReactMarkdown>
                                                    {msg.created_at && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'block', mt: 0.5, opacity: 0.7, fontFamily, fontSize: '0.68rem',
                                                                textAlign: isUser ? 'right' : 'left',
                                                                color: isUser ? 'rgba(255,255,255,0.8)' : '#94a3b8',
                                                            }}
                                                        >
                                                            {formatTime(msg.created_at)}
                                                        </Typography>
                                                    )}
                                                </Paper>
                                                {isUser && (
                                                    <Avatar sx={{ bgcolor: '#6366f1', width: 30, height: 30, mt: 0.5 }}>
                                                        <PersonIcon sx={{ fontSize: 16 }} />
                                                    </Avatar>
                                                )}
                                            </Box>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </Box>
                        </>
                    )}
                </Paper>
            </Box>
        </Box>
    );
}
